import Paper from "@mui/material/Paper";
import { alpha, type Theme } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";
import type { ReactNode } from "react";
import { softShadow } from "../../../../theme";

const PAD = { sm: 1.5, md: 2.5, lg: 4 } as const;

interface PanelProps {
  tone?: "default" | "muted" | "accent";
  pad?: keyof typeof PAD;
  /** Takes the full height of its container, as a column, so that panels side by side are as tall
   *  as the tallest and what they hold can grow into the room. */
  fill?: boolean;
  children: ReactNode;
}

function toneStyles(theme: Theme, tone: NonNullable<PanelProps["tone"]>) {
  switch (tone) {
    case "muted":
      return { backgroundColor: theme.palette.background.default };
    case "accent":
      return { backgroundColor: alpha(theme.palette.primary.main, 0.08) };
    default:
      return { backgroundColor: theme.palette.background.paper, boxShadow: softShadow };
  }
}

const PanelPaper = styled(Paper)<{
  $tone: NonNullable<PanelProps["tone"]>;
  $pad: keyof typeof PAD;
  $fill: boolean;
}>(({ theme, $tone, $pad, $fill }) => ({
  padding: theme.spacing(PAD[$pad]),
  ...($fill && { height: "100%", display: "flex", flexDirection: "column" }),
  ...toneStyles(theme, $tone),
}));

/**
 * The standard surface primitive: a padded container that lifts off the canvas
 * with a soft ambient shadow (default tone) or a flat tint (muted / accent).
 * Knobs are closed (`tone`, `pad`) and map to theme tokens; no `sx` passthrough.
 */
export function Panel({ tone = "default", pad = "md", fill = false, children }: PanelProps) {
  return (
    <PanelPaper elevation={0} $tone={tone} $pad={pad} $fill={fill}>
      {children}
    </PanelPaper>
  );
}
